import { useEffect, useState } from 'react';
import { Avatar } from './Avatar';
import { downloadUrl } from '../files/api';

/**
 * Somebody's face, from the copy this product keeps (Phase 93; moved out of the inbox in 101).
 *
 * WhatsApp serves a profile picture behind a signed link that expires in minutes, so a thread left
 * open renders it as a broken image — which is worse than initials, because it reads as something
 * wrong with the product. The bytes are stored now, and this prefers that copy.
 *
 * The provider's URL is still the fallback: it is what there is before the first copy exists, and
 * what carries a picture the CDN served but this product could not keep.
 *
 * The signed link is asked for per avatar that is actually drawn, exactly as an attachment does.
 *
 * It started life in the inbox, where the picture arrives. It lives here because the person it
 * belongs to does not: the same face is drawn on their contact record and in a list of contacts,
 * and a CRM screen reaching into the inbox module for a component would be the wrong dependency.
 */
export function StoredAvatar({
  name,
  fileId,
  url,
  size,
}: {
  name: string;
  fileId?: string | null;
  url?: string | null;
  size?: 'sm' | 'md' | 'lg';
}): React.JSX.Element {
  const [stored, setStored] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setStored(null);

      return;
    }

    let cancelled = false;

    downloadUrl(fileId)
      .then((ticket) => {
        if (!cancelled) setStored(ticket.url);
      })
      .catch(() => {
        // The provider's URL is still there, and initials are still there behind that.
        if (!cancelled) setStored(null);
      });

    return () => {
      cancelled = true;
    };
  }, [fileId]);

  return <Avatar name={name} url={stored ?? url ?? null} size={size} />;
}
